import { Bank, ChartLineUp, Coins, CreditCard, Cube, Diamond, House, Money } from '@phosphor-icons/react';

import { CardErrorState } from '../common/CardErrorState';
import { InfoTooltip } from '../common/InfoTooltip';
import { MoneyDisplay } from '../common/MoneyDisplay';
import { PatrimonioDetailLinks } from './PatrimonioDetailLinks';
import { cn } from '../../utils/cn';
import pt from '../../locales/pt';

/** .pen `ds/label/section-heading`. */
function SectionHeading({ title, meta }) {
  return (
    <div className="flex items-center gap-space-2">
      <p className="type-section text-muted-foreground">{title}</p>
      {meta ? <p className="type-chip text-muted-foreground">{meta}</p> : null}
    </div>
  );
}

const ASSET_BUCKET_ICONS = {
  conta: Bank,
  corretora: ChartLineUp,
  crypto: Coins,
  ouro: Diamond,
  imobiliario: House,
  outro: Cube,
};

/**
 * Item de composição — `.pen` `ds/row/composition-item` (`dN753`), grelha de
 * 4 zonas: marcador · texto · valor · cauda.
 *
 * Tokens do `.pen`:
 *   row   stroke top `$border` · gap `$space-3` · padding `[$space-3, 0]` · center
 *   chip  28×28 · `$surface-muted` · `$radius-ds-sm` · stroke `$border`
 *     i   16 · `$fg-muted` (dívidas: `$danger-tint` / `$danger-fg`)
 *   m     fill_container · vertical · gap 2
 *     n   `$font-display` `$type-display-row` 500 · `$fg-card`
 *     s   `$type-label` normal · `$fg-muted`
 *   r     vertical · gap 2 · fim
 *     a   `$font-display` `$type-display-row` 600 · `$fg-card`
 *     p   `$type-label` normal · `$fg-muted`
 */
function CompositionItem({ icon: Icon, tone, name, subtitle, value, pct }) {
  const isDebt = tone === 'debt';

  return (
    <div className="flex items-center gap-space-3 border-t border-border py-space-3 first:border-t-0">
      <span
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-ds-sm border border-border',
          isDebt ? 'bg-danger-tint text-danger-fg' : 'bg-muted text-muted-foreground',
        )}
      >
        <Icon className="h-4 w-4" aria-hidden />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="type-display-row truncate font-medium text-card-foreground">
          {name}
        </p>
        {subtitle ? (
          <p className="type-label-meta truncate text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-col items-end gap-0.5 text-right">
        <MoneyDisplay
          value={value}
          size="sm"
          className="type-display-row font-semibold text-card-foreground"
        />
        {pct != null ? (
          <p className="type-label-meta text-muted-foreground">
            {pt.patrimonio.composition.pctOfAssets.replace('{pct}', pct)}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Cartão de composição — `.pen` `ds/card/composition-group` (`LCiBo`),
 * gramática da família group-card:
 *   marcador → cabeçalho com total → itens → (erro).
 *
 * Tokens do `.pen`:
 *   card  `$surface-card` · `$radius-ds-lg` · stroke `$border`
 *   head  gap `$space-3` · padding `[$space-4, $space-5]` · space_between · center
 *     dot 8×8 · `$radius-ds-full`
 *     t   `$font-display` `$type-title-card` 600 · `$fg-card`
 *     i   info 14 · `$fg-muted`
 *     total `$font-display` `$type-display-stat` 600 · `$fg-card`
 *   sep   rectângulo 1px `$border`
 *   rows  padding `[0, $space-5, $space-4, $space-5]`
 */
function CompositionGroup({
  dotClassName,
  label,
  tooltip,
  total,
  totalClassName,
  isError = false,
  onRetry,
  children,
}) {
  return (
    <section className="flex w-full min-w-0 flex-col overflow-hidden rounded-ds-lg border border-border bg-card">
      <div className="flex min-w-0 items-center justify-between gap-space-3 px-space-5 py-space-4">
        <div className="flex min-w-0 flex-1 items-center gap-space-2">
          <span
            className={cn('h-2 w-2 shrink-0 rounded-ds-full', dotClassName)}
            aria-hidden
          />
          <p className="type-title-card truncate font-display text-card-foreground">
            {label}
          </p>
          {tooltip ? <InfoTooltip text={tooltip} className="h-3.5 w-3.5" /> : null}
        </div>
        <MoneyDisplay
          value={total}
          size="sm"
          className={cn('type-display-stat shrink-0 font-semibold', totalClassName)}
        />
      </div>

      <div className="h-px w-full bg-border" aria-hidden />

      {isError ? (
        <CardErrorState title={pt.patrimonio.composition.loadError} onRetry={onRetry} />
      ) : (
        <div className="flex min-w-0 flex-col px-space-5 pb-space-4">{children}</div>
      )}
    </section>
  );
}

export function PatrimonioCompositionSection({
  assetGroups = [],
  liabilityItems = [],
  totalAssets = 0,
  totalLiabilities = 0,
  snapshotDateLabel = null,
  stackedLinks = false,
  isError = false,
  onRetry,
  className,
}) {
  const hasAssets = assetGroups.length > 0;
  const hasDebts = liabilityItems.length > 0;
  const isEmpty = !hasAssets && !hasDebts;

  return (
    <div className={cn('flex min-w-0 flex-col gap-space-5', className)}>
      <div
        className="flex min-w-0 flex-col gap-space-5"
        data-testid="patrimonio-composition-panel"
      >
        {isError ? (
          <>
            <SectionHeading title={pt.patrimonio.composition.title} />
            <div className="w-full min-w-0 overflow-hidden rounded-ds-lg border border-border bg-card">
              <CardErrorState
                title={pt.patrimonio.composition.loadError}
                onRetry={onRetry}
              />
            </div>
          </>
        ) : isEmpty ? (
          <p className="type-body py-space-4 text-center text-muted-foreground">
            {pt.patrimonio.composition.emptyAssets}
          </p>
        ) : (
          <>
            <SectionHeading
              title={pt.patrimonio.composition.title}
              meta={snapshotDateLabel ? `· última selfie · ${snapshotDateLabel}` : null}
            />

            <div className="flex flex-col gap-space-3">
              {hasAssets ? (
                <CompositionGroup
                  dotClassName="bg-sage-500"
                  label={pt.patrimonio.composition.assets}
                  tooltip={pt.patrimonio.composition.assetsTooltip}
                  total={totalAssets}
                  totalClassName="text-card-foreground"
                >
                  {assetGroups.map((group) => {
                    const Icon = ASSET_BUCKET_ICONS[group.id] || Money;
                    return (
                      <CompositionItem
                        key={group.id}
                        icon={Icon}
                        name={group.label}
                        subtitle={group.subtitle}
                        value={group.value}
                        pct={group.pct}
                      />
                    );
                  })}
                </CompositionGroup>
              ) : null}

              {hasDebts ? (
                <CompositionGroup
                  dotClassName="bg-group-debt"
                  label={pt.patrimonio.composition.debts}
                  tooltip={pt.patrimonio.composition.debtsTooltip}
                  total={totalLiabilities}
                  totalClassName="text-card-foreground"
                >
                  {liabilityItems.map((item) => (
                    <CompositionItem
                      key={item.id}
                      icon={CreditCard}
                      tone="debt"
                      name={item.name}
                      subtitle={item.subtitle}
                      value={item.value}
                    />
                  ))}
                </CompositionGroup>
              ) : null}
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col gap-space-5">
        <SectionHeading title={pt.patrimonio.composition.manageDetail} />
        <PatrimonioDetailLinks stacked={stackedLinks} />
      </div>
    </div>
  );
}
